import { FunctionComponent, useState } from 'react';
import { Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { ColorPalette } from 'shared/consts/colorPalette';
import { CategoryList } from 'widgets/startRecognition/ui/CategoryList';
import { Category } from 'widgets/startRecognition/consts/CategoryList';
import { DocsUploadModal } from 'widgets/startRecognition/ui/DocsUploadModal';
import { PageHeader } from 'shared/ui/PageHeader';
import { GuideModal } from 'widgets/startRecognition/ui/GuideModal';
import { SessionsDashboard } from 'widgets/startRecognition/ui/SessionsDashboard';
import { useImportFilesStore } from 'widgets/importFiles/state';
import { useCustomModelStore } from 'widgets/customModel/state';
import { getImportSessions } from 'entities/ImportSessions/api';
import { getCustomModels } from 'entities/CustomModel/api';
import { getGenerativeModels } from 'entities/generative-models/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ModelStatuses } from 'entities/CustomModel/types';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

export const StartRecognition: FunctionComponent = () => {
	const { t } = useTranslation();
	const { data, setData } = useImportFilesStore();
	const { setCustomModels, setGenerativeModels } = useCustomModelStore();
	const [uploadCategory, setUploadCategory] = useState<Category>();

	useQuery({
		queryKey: ['get/import-sessions'],
		queryFn: () => getImportSessions(),
		onSuccess: (response) => {
			setData(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	useQuery({
		queryKey: ['get/custom-models'],
		queryFn: () => getCustomModels({ status: ModelStatuses.trained }),
		onSuccess: (response) => {
			setCustomModels(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	useQuery({
		queryKey: ['get/generative-models'],
		queryFn: () => getGenerativeModels({ status: ModelStatuses.trained }),
		onSuccess: (response) => {
			setGenerativeModels(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const handleOpenDocsUploadModal = (category: Category) => setUploadCategory(category);
	const handleCloseDocsUploadModal = () => setUploadCategory(undefined);

	return (
		<PageWrapper>
			<PageHeader
				title={t('start_recognition.title')}
				description={t('start_recognition.description')}
				additional={<GuideModal />}
			/>

			<CategoryList openUploadModal={handleOpenDocsUploadModal} />

			{uploadCategory && (
				<DocsUploadModal
					isActiveSessions={Boolean(data?.length)}
					category={uploadCategory}
					handleClose={handleCloseDocsUploadModal}
				/>
			)}
			<Divider color={ColorPalette.periwinkle} sx={{ margin: '20px 0' }} />

			<SessionsDashboard sessions={data ?? []} />
		</PageWrapper>
	);
};
